import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Users, UserPlus, Plus, Edit2, Trash2, LogOut, Briefcase, AlertCircle, Settings, ArrowRight, ArrowLeft,
} from '../../icons';
import {
  listTeams, listTeamMembers, patchTeam, deleteTeam,
  patchTeamMemberRole, removeTeamMember,
  type TeamDto, type TeamMemberDto, type ProjectTeamRole,
} from '../../api/project-teams';
import { listProjects, type TaskProjectDto } from '../../api/tasks';
import { useAuthStore } from '../../store/authStore';
import { DetailPageHeader } from './DetailPageHeader';
// AW-003 (2026-08-18) — "팀 생성이 너무 복잡하다" 피드백에 따라 3단계 위저드 대신
// 한 장짜리 생성+초대 모달을 쓴다. TeamSetupWizardModal 파일은 보존(롤백 지점).
// ⭐ 정본 이관 판 2 (2026-10-13 · 사용자 「판 2 진행」) — 정본 TeamsDetailPage.tsx:15 그대로.
//   되돌리기 ⇒ 이 import 와 아래 <TeamCreateInviteModal> 한 자리를 TeamSetupWizardModal(onFinished) 로.
import { TeamCreateInviteModal } from './TeamCreateInviteModal';
import { PROJECT_TEAMS_SHELL } from '../../lib/task-shell';
import { TeamsOnboarding } from './TeamsOnboarding';
import { TeamMemberInviteModal } from './TeamMemberInviteModal';
import { ProjectCreateSimpleModal } from './ProjectCreateSimpleModal';
import { stripColor } from '../TaskPage/ProjectTopBar';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { confirmAndDeleteProject, isProjectCreator } from './confirmDeleteProject';
import { useModalLeave } from '../../hooks/useModalLeave';
import { useProjectTeamsSignal } from './project-teams-context';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { tProject, getProjectLang, subscribeProjectLang } from '../../lib/project-i18n';
import './ProjectPage.css';

function errCode(err: unknown): string | undefined {
  return (err as { response?: { data?: { code?: string } } })?.response?.data?.code;
}
function errMsg(err: unknown, fallback: string): string {
  return (err as { response?: { data?: { message?: string } } })?.response?.data?.message
    ?? (err as Error)?.message ?? fallback;
}

/** 팀 평균 진행률(%) — listProjects 의 doneTaskCount/taskCount 합산 (백엔드 무변경). */
function teamProgressPct(projects: TaskProjectDto[], teamId: string): number | null {
  const mine = projects.filter((p) => p.teamId === teamId);
  const total = mine.reduce((s, p) => s + (p.taskCount ?? 0), 0);
  if (total === 0) return mine.length > 0 ? 0 : null;
  const done = mine.reduce((s, p) => s + (p.doneTaskCount ?? 0), 0);
  return Math.round((done / total) * 100);
}

/** 진행률 링 — 인라인 SVG (StatsDetailPage 게이지 축소 동형, 이모지 금지). */
function ProgressRing({ pct }: { pct: number | null }) {
  const R = 11;
  const C = 2 * Math.PI * R;
  const filled = pct === null ? 0 : (C * pct) / 100;
  return (
    <span
      className="pj-ring"
      role="img"
      aria-label={tProject('project.team.detail.avg_progress_aria', { percent: pct ?? 0 })}
    >
      <svg width="28" height="28" viewBox="0 0 28 28" aria-hidden="true">
        <circle cx="14" cy="14" r={R} fill="none" stroke="var(--border)" strokeWidth="3.5" />
        {/* filled > 0 조건 — 0% 에서 strokeLinecap="round" 가 길이 0 인 호를 점 하나로 남긴다. */}
        {pct !== null && filled > 0 && (
          <circle
            cx="14" cy="14" r={R} fill="none"
            stroke="var(--accent-green, #10b981)" strokeWidth="3.5" strokeLinecap="round"
            strokeDasharray={`${filled} ${C - filled}`}
            transform="rotate(-90 14 14)"
          />
        )}
      </svg>
      <span className="pj-ring-num">{pct === null ? '—' : `${pct}%`}</span>
    </span>
  );
}

/** 아바타 스택 — 최대 3 + 초과 +N. avatarUrl 은 api 매핑에서 해석 완료. */
function AvatarStack({ members, total }: { members: TeamMemberDto[]; total: number }) {
  const shown = members.slice(0, 3);
  const extra = total - shown.length;
  return (
    <span className="pj-avatar-stack" aria-hidden="true">
      {shown.map((m) => (
        m.avatarUrl ? (
          <img key={m.userId} className="pj-avatar-stack-item" src={m.avatarUrl} alt="" />
        ) : (
          <span key={m.userId} className="pj-avatar-stack-item pj-avatar-stack-item--initial">
            {m.name.slice(0, 1)}
          </span>
        )
      ))}
      {extra > 0 && (
        <span className="pj-avatar-stack-item pj-avatar-stack-item--more">
          +{extra}
        </span>
      )}
    </span>
  );
}

/**
 * TeamsDetailPage (project-menu v2.1 — /project/teams) — 팀 카드 드릴다운 상세, 시각 강화 C안.
 *
 * v2 대비 변경 (사용자 시안 선택 2026-06-10):
 *   - 좌 팀 카드: 진행률 링(팀 프로젝트 done/task 집계) + 멤버 아바타 스택
 *   - 우 패널: 수정/삭제 버튼 → ⚙ 설정 드롭다운 (LEADER), 멤버 목록 → 카드 그리드,
 *     선택 팀 프로젝트 미니 카드(진행률 바) 섹션 추가
 * 관리 액션·409 안내(TEAM_HAS_ACTIVE_PROJECTS·LAST_LEADER_PROTECTED)는 v2 와 동일.
 */
export function TeamsDetailPage() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  /*
   * ⭐ 2026-10-12 — 팀 목록이 바뀌면 «셸»에도 알린다 (사용자 지적).
   *   ⇒ 종전에는 refreshTeams() 가 «이 화면 자기 상태»만 다시 읽어, 서브메뉴(셸)는 낡았다.
   *   ⇒ 셸 밖에서 그려지면 기본값이 빈 함수다 — 그냥 불러도 된다(project-teams-context 머리말).
   * ⚠ refreshTeams() 를 «대신하지» 않는다. 두 벌을 그대로 두고 신호만 이었다 ⇒ 둘 다 부른다.
   */
  const { teamsChanged } = useProjectTeamsSignal();
  const navigate = useNavigate();
  const me = useAuthStore((s) => s.user);

  /**
   * ⭐⭐ **`?team=` 을 ★ «읽는다»** (2026-10-08).
   *
   * ## ⛔ 무엇이 깨져 있었나 — ★ 보내는 곳 넷, ⭐ 받는 곳 «0»
   *
   * ```
   * 보내는 곳 ★ 넷 — 전부 /project/teams?team=<id> 로 온다
   *   ProjectNavShell       2차 메뉴의 워크스페이스 한 줄
   *   ProjectPage           홈에서 팀 누르기
   *   ProjectSearchPage     ⭐ 통합 검색 결과의 «스페이스» 누르기
   *   TeamMemberBadge       업무 화면의 팀 배지
   * 받는 곳  ⛔ 0건 — 이 파일에 useSearchParams·location.search·URLSearchParams 가 ★ 전부 0
   * ⇒ ⇒ ★ 그래서 ⭐ «어느 팀을 눌러도 첫 팀»이 열렸다(아래 자동 선택이 teams[0] 을 집는다).
   * ```
   * ⇒ ★ 「낱말은 네 칸으로 센다 — ⭐ 받는 칸이 0이면 죽은 것」의 실물이다.
   * ⇒ ⭐ 꼴은 **같은 폴더의 `ProjectsDetailPage`** 를 따랐다(그 화면은 이 `?team=` 을 제대로 읽는다)
   *   — ⛔ 새 규칙을 만들지 않았다.
   *
   * ## ⚠⚠ 「`useState(처음 값)`」으로는 ⛔ 모자란다 — ⭐ 그리고 ★ 그것은 ⭐ «필요도 없었다»
   *
   * 이 화면은 **그 메뉴가 떠 있는 채로** 보인다(`ProjectNavShell`). ⇒ `/project/teams?team=12`
   * 에서 `?team=13` 을 누르면 ★ **같은 라우트**라 부품이 ⭐ «다시 마운트되지 않는다».
   * ⇒ ⇒ ⛔ `useState(처음값)` 만 두면 ★ 두 번째 누름이 안 먹는다. ⇒ 그래서 **효과**가 필요하다.
   *
   * ⚠ 처음에는 **둘 다**(처음 값 ＋ 효과) 두었다 — 「첫 그림에서 teams[0] 이 깜빡이지 않게」라는
   *   까닭을 붙였다. ⇒ ⛔ **사보타주가 그 까닭을 물렸다**: 처음 값«만» 버려도 ★ 시험이 ⭐ 전부
   *   초록이었다(효과가 다 한다). ⇒ ⇒ ★ 그리고 ⭐ 그 「깜빡임」은 ★ 일어나지도 않는다 —
   *   그 시점에 `teams` 가 아직 비어 있어 **왼쪽 목록도 빈 화면**이고, 자동 선택도 못 돈다.
   * ⇒ ★ 그래서 ⭐ 처음 값을 **뺐다**(원래 줄 그대로 `null`). ⇒ ⚠ 「재지 못하는 이득」을
   *   코드로 남기지 않는다 — 그러면 다음 사람이 그것을 ⛔ 「필요한 것」으로 읽는다.
   *
   * ## ⛔ 아래 자동 선택(teams[0])은 ★ 그대로 둔다 — ⭐ 지우면 «회복 길»이 사라진다
   *
   * 그 주석이 까닭을 적어 둔다: 「삭제·탈퇴로 선택이 상실된 경우에도 동작(`selected === null`
   * 기준이라 목록에 없는 stale id 도 회복)」. ⇒ ★ 즉 `?team=` 에 **지워진 팀 id** 가 와도
   * ⭐ 그 효과가 teams[0] 으로 회복한다. ⇒ ⛔ 그 길을 막지 않았다.
   */
  const [searchParams] = useSearchParams();
  // ⚠ useSearchParams 가 퍼센트 인코딩을 알아서 푼다 — 보내는 넷 중 하나만 encodeURIComponent 를
  //   쓰는데(TeamMemberBadge), 읽는 쪽은 ★ 둘 다 같은 값으로 받는다.
  const teamFromUrl = searchParams.get('team');

  const [teams, setTeams] = useState<TeamDto[]>([]);
  // v2.5 — 최초 로드 완료 전 온보딩(빈 상태) 깜빡임 방지.
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [members, setMembers] = useState<TeamMemberDto[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  /** 팀 카드 아바타 스택용 — 팀별 멤버 미리보기 (최초 1회 병렬 로드). */
  const [memberPreview, setMemberPreview] = useState<Record<string, TeamMemberDto[]>>({});
  const [projects, setProjects] = useState<TaskProjectDto[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  // 팀 상세에서 프로젝트 바로 추가 (2026-06-10 사용자 요청) — 선택 팀 사전선택.
  const [creatingProject, setCreatingProject] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  // 메타 수정 인라인 폼
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const createModal = useModalLeave(creating || null, 200);
  // useModalLeave 의 effect deps 가 open 을 참조 동일성으로 추적 — 인라인 객체 리터럴을
  // 넘기면 모달 open 동안 매 렌더 새 참조 → setLatched 무한 루프 (테스트 행으로 발견).
  // useMemo 로 참조 안정화 (열림 상태/selectedTeamId 변경 시에만 재생성).
  const inviteInput = useMemo(
    () => (inviting ? { teamId: selectedTeamId } : null),
    [inviting, selectedTeamId],
  );
  const inviteModal = useModalLeave(inviteInput, 200);
  const projectInput = useMemo(
    () => (creatingProject ? { teamId: selectedTeamId } : null),
    [creatingProject, selectedTeamId],
  );
  const projectModal = useModalLeave(projectInput, 200);
  useBodyScrollLock(createModal.shouldRender || inviteModal.shouldRender || projectModal.shouldRender);

  const selected = selectedTeamId === null ? null : teams.find((t) => t.id === selectedTeamId) ?? null;
  const isLeader = selected !== null && (selected.myRole === 'LEADER' || selected.myRole === 'ADMIN_BYPASS');
  const selectedProjects = useMemo(
    () => (selected ? projects.filter((p) => p.teamId === selected.id) : []),
    [projects, selected],
  );

  /*
   * ⭐⭐ 2026-10-12 — 이 손이 끝나면 «셸»에도 알린다 (사용자 지적: 「팀을 삭제하면 스페이스
   *   서브메뉴에 나오지만 새로고침하지 않으면 삭제가 되지 않는다」).
   *
   * ## 왜 «자리마다»가 아니라 여기인가
   *
   * ⭐ 2026-10-12 정정 — 아래 「10」은 틀렸다. 실측 ⇒ 11
   *   (mask-code.mjs 로 덮은 뒤 ⇒ :269 :333 :359 :373 :392 :395 :426 :718 :809 :821 :835)
   *   ⇐ 종전 「10」은 `void refreshTeams();` 로만 세어 :426 의 «세미콜론 없는» 꼴
   *     (onClick={() => void refreshTeams()})을 빠뜨린 수다. 날것은 13(정의·주석 포함).
   *   ⇒ 설계는 안 바뀐다 — 구간 안에 한 번이니 몇이든 덮는다. 수만 정정한다.
   *
   * 
   *
   * ⚠ 마운트 때도 한 번 돈다(아래 useEffect) ⇒ 셸도 한 번 더 읽는다.
   *   ⇒ 그 값은 셸이 이미 읽은 것과 같고, 비용은 요청 하나다 ⇒ 받아들였다.
   *   ⇒ ⇒ 가드는 그래서 「지운 «뒤»에 한 번 더 불렸나」를 «수»로 잰다(1 ⇒ 2).
   */
  const refreshTeams = useCallback(async () => {
    setTeamsError(null);
    try {
      const list = await listTeams();
      setTeams(list);
      // ⇒ 셸(서브메뉴)도 다시 읽게 한다. 아바타 읽기를 기다리지 않는다 —
      //   서브메뉴는 팀 «이름»만 그린다(ProjectHomeNav 의 teams.map ⇒ t.name).
      teamsChanged();
      // 아바타 스택 — 팀별 멤버 병렬 로드 (팀 수 소규모 전제, 상한 10).
      const targets = list.slice(0, 10);
      const results = await Promise.allSettled(targets.map((t) => listTeamMembers(t.id)));
      const next: Record<string, TeamMemberDto[]> = {};
      results.forEach((r, i) => {
        const team = targets[i];
        if (r.status === 'fulfilled' && team) next[team.id] = r.value;
      });
      setMemberPreview(next);
    } catch (err) {
      setTeamsError(errMsg(err, tProject('project.team.err_fetch')));
    } finally {
      setTeamsLoaded(true);
    }
    // ⚠ `teamsChanged` 는 셸의 useCallback([]) 이라 안정적이다 ⇒ 이 손이 새로 만들어지지 않는다.
    //   ⇒ 그래서 이것을 부르는 마운트 효과도 한 번만 돈다(무한 되읽기가 생기지 않는다).
  }, [teamsChanged]);

  const refreshProjects = useCallback(async () => {
    try {
      setProjects(await listProjects());
    } catch {
      /* 진행률 링·미니 카드만 영향 — '—' 표시로 graceful. */
    }
  }, []);

  const refreshMembers = useCallback(async (teamId: string) => {
    setMembersLoading(true);
    try {
      const list = await listTeamMembers(teamId);
      setMembers(list);
      setMemberPreview((prev) => ({ ...prev, [teamId]: list }));
    } catch {
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshTeams();
    void refreshProjects();
  }, [refreshTeams, refreshProjects]);

  useEffect(() => {
    setActionError(null);
    setEditing(false);
    setMenuOpen(false);
    if (selectedTeamId === null) { setMembers([]); return; }
    void refreshMembers(selectedTeamId);
  }, [selectedTeamId, refreshMembers]);

  /**
   * ⭐ 2026-10-08 — 주소의 `?team=` 이 **바뀌면** 선택을 거기로 맞춘다.
   *
   * ⚠ 이것이 **없으면** 같은 화면에서 ★ 다른 팀을 눌러도 안 바뀐다 — 같은 라우트라
   *   부품이 ⭐ 다시 마운트되지 않아 `useState(처음값)` 이 ⛔ 다시 안 쓰인다.
   * ⚠ `teamFromUrl` 이 **빈 값일 때는 아무것도 하지 않는다** — 그때 선택을 지우면
   *   ⇒ ⛔ 화면 안에서 카드를 눌러 고른 것이 ★ 주소 때문에 풀린다(주소는 그때 안 바뀐다).
   *   ⇒ ★ 그 자리는 아래 자동 선택이 맡는다.
   */
  useEffect(() => {
    if (teamFromUrl) setSelectedTeamId(teamFromUrl);
  }, [teamFromUrl]);

  // v2.2 (2026-06-10 사용자 요청) — 선택 팀이 없으면 최상단 팀 자동 선택.
  // 온로드뿐 아니라 삭제·탈퇴로 선택이 상실된 경우에도 동작 (selected === null 기준이라
  // 목록에 없는 stale id 도 회복). 선택되면 위 effect 가 멤버·프로젝트 조회를 트리거.
  // ⚠ 2026-10-08 — 위 `?team=` 효과와 «짝»이다. 순서가 뜻을 가진다:
  //   ?team= 이 목록에 «있는» id 면 ⇒ selected 가 잡히므로 ★ 이 효과는 안 끼어든다.
  //   ?team= 이 «지워진» id 면      ⇒ selected 가 null 이라 ⇒ ⭐ 여기서 teams[0] 으로 회복한다.
  //   ⇒ ⛔ 그래서 이 효과를 지우면 «북마크한 죽은 링크»가 ★ 빈 화면이 된다.
  //
  // ⛔⛔ 조건이 `selected === null` 이고 ★ `selectedTeamId === null` 이 ⭐ «아니다» — 한 단어가
  //   뜻을 가른다. ⇒ `selectedTeamId` 로 바꾸면 ★ 위 효과가 넣어 둔 «죽은 id» 가 ⇒ null 이 아니므로
  //   ⇒ ⇒ ⛔ 회복이 «안 돈다»(빈 화면). ⇒ ⭐ 2026-10-08 사보타주로 실측했다: 그 한 단어를 바꾸면
  //   ★ 「없는 id 가 와도 빈 화면이 아니다」 시험 «하나»만 빨개진다(「?team= 없으면 첫 팀」은 초록).
  //   ⇒ ★ 즉 그 둘은 ⭐ «다른 길»을 잰다 — 선택이 «처음부터 없던» 길과 «죽은 값이 들어온» 길.
  //   ⇒ ⚠ 그리고 뒤쪽 길은 ⭐ 위 `?team=` 효과가 ★ «새로 만든» 길이다(전에는 삭제·탈퇴뿐이었다).
  useEffect(() => {
    if (selected === null && teams.length > 0) {
      const first = teams[0];
      if (first) setSelectedTeamId(first.id);
    }
  }, [teams, selected]);

  // ⚙ 드롭다운 외부 클릭 닫기 (Combobox 동형).
  useEffect(() => {
    if (!menuOpen) return;
    const onDocMouseDown = (ev: MouseEvent) => {
      if (menuRef.current?.contains(ev.target as Node) === false) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [menuOpen]);

  async function handleSaveMeta(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || !editName.trim()) return;
    setSaving(true);
    setActionError(null);
    try {
      await patchTeam(selected.id, { name: editName.trim(), description: editDesc.trim() || undefined });
      setEditing(false);
      void refreshTeams();
    } catch (err) {
      setActionError(errMsg(err, tProject('project.team.edit.err_save')));
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteTeam() {
    if (!selected) return;
    // v2.3 (2026-06-11) — 팀 내 프로젝트 존재 여부·이름 미리보기를 삭제 컨펌에 표시.
    const n = Math.max(selected.projectCount, selectedProjects.length);
    let detail: string;
    if (n > 0) {
      let names = selectedProjects.slice(0, 3).map((p) => p.name).join(', ');
      if (n > 3) names += tProject('project.delete.more_suffix', { n: n - 3 });
      detail = tProject('project.team.delete.projects_warn', { n, names: names || '…' });
    } else {
      detail = tProject('project.team.delete.projects_none');
    }
    const msg = `${tProject('project.team.delete.confirm', { name: selected.name })}\n${detail}`;
    if (!(await layerConfirm(msg, { danger: true }))) return;
    setActionError(null);
    try {
      await deleteTeam(selected.id);
      setSelectedTeamId(null);
      void refreshTeams();
    } catch (err) {
      setActionError(errCode(err) === 'TEAM_HAS_ACTIVE_PROJECTS'
        ? tProject('project.team.delete.err_active')
        : errMsg(err, tProject('project.team.delete.err')));
    }
  }

  async function handleRoleChange(m: TeamMemberDto, role: ProjectTeamRole) {
    if (!selected || m.role === role) return;
    setActionError(null);
    try {
      await patchTeamMemberRole(selected.id, m.userId, role);
      void refreshMembers(selected.id);
      void refreshTeams();
    } catch (err) {
      setActionError(errCode(err) === 'LAST_LEADER_PROTECTED'
        ? tProject('project.team.member.err_last_leader')
        : errMsg(err, tProject('project.team.member.err')));
    }
  }

  async function handleRemove(m: TeamMemberDto, isSelf: boolean) {
    if (!selected) return;
    const ok = await layerConfirm(isSelf
      ? tProject('project.team.member.leave_confirm')
      : tProject('project.team.member.remove_confirm', { name: m.name }), { danger: true });
    if (!ok) return;
    setActionError(null);
    try {
      await removeTeamMember(selected.id, m.userId);
      if (isSelf) {
        setSelectedTeamId(null);
        void refreshTeams();
      } else {
        void refreshMembers(selected.id);
        void refreshTeams();
      }
    } catch (err) {
      setActionError(errCode(err) === 'LAST_LEADER_PROTECTED'
        ? tProject('project.team.member.err_last_leader')
        : errMsg(err, tProject('project.team.member.err')));
    }
  }

  // v2.5.2 — 팀 0개(온보딩 노출)면 헤더 [새 팀] 숨김 (CTA 는 온보딩 본문 하나로 단일화).
  const showOnboarding = teamsLoaded && teams.length === 0 && !teamsError;

  return (
    <div className={`pj-page${PROJECT_TEAMS_SHELL ? ' pj-page--shell' : ''}`}>
      {/* 셸(design D-4 A안)에서는 페이지 헤더 대신 좌측 팀 패널이 제목·[새 팀]을 갖는다 —
          내 프로젝트 화면과 같은 문법. 플래그 OFF 면 기존 헤더 그대로(무회귀). */}
      {!PROJECT_TEAMS_SHELL && (
        <DetailPageHeader
          title={tProject('project.detail.teams.title')}
          actions={showOnboarding ? undefined : (
            <button type="button" className="pj-button pj-button--primary" onClick={() => setCreating(true)}>
              <UserPlus size={14} />
              {tProject('project.btn.new_team')}
            </button>
          )}
        />
      )}

      {teamsError && (
        <div className="pj-error" role="alert">
          <AlertCircle size={14} /> {teamsError}
          <button type="button" className="pj-button" onClick={() => void refreshTeams()}>
            {tProject('project.btn.retry')}
          </button>
        </div>
      )}

      {/* v2.5 (시안② 확정) — 팀 0개면 온보딩 3스텝, 아니면 기존 2컬럼 상세 */}
      {showOnboarding ? (
        <TeamsOnboarding onStart={() => setCreating(true)} />
      ) : (
      <div className={`pj-teams-detail${PROJECT_TEAMS_SHELL ? ' pj-teams-detail--shell' : ''}`}>
        {/* 좌 — 팀 카드 목록 (진행률 링 + 아바타 스택) */}
        <section className="pj-teams-detail-list scrollbar-thin" aria-label={tProject('project.team.heading')}>
          {PROJECT_TEAMS_SHELL && (
            <div className="pj-teams-side-head">
              {/* 3c 프로젝트 홈으로 복귀(2026-07-25) — 팀 화면은 홈의 '내 팀' 에서 내려온 자리다. */}
              <button
                type="button"
                className="pj-teams-side-back"
                onClick={() => navigate('/project')}
                aria-label={tProject('project.home.back_home_aria')}
                title={tProject('project.home.back_home_aria')}
              >
                <ArrowLeft size={13} />
                {tProject('project.home.back_home')}
              </button>
              <div className="pj-teams-side-titlerow">
                <span className="pj-teams-side-title">{tProject('project.detail.teams.title')}</span>
                <button
                  type="button"
                  className="pj-teams-side-new"
                  onClick={() => setCreating(true)}
                  title={tProject('project.btn.new_team')}
                  aria-label={tProject('project.btn.new_team')}
                >
                  <UserPlus size={14} />
                </button>
              </div>
            </div>
          )}
          {teams.length === 0 && (
            <div className="pj-empty" role="status">{tProject('project.team.empty')}</div>
          )}
          <ul className="pj-team-cards">
            {teams.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={`pj-team-card${selectedTeamId === t.id ? ' pj-team-card--active' : ''}`}
                  style={{ '--pj-strip': stripColor(t.id) } as React.CSSProperties}
                  onClick={() => setSelectedTeamId(t.id)}
                >
                  <span className="pj-team-card-top">
                    <span className="pj-team-card-name">
                      <Users size={14} />
                      {t.name}
                    </span>
                    <ProgressRing pct={teamProgressPct(projects, t.id)} />
                  </span>
                  <span className="pj-team-card-meta">
                    <span className={`pj-role-badge${t.myRole === 'LEADER' ? ' pj-role-badge--leader' : ''}`}>
                      {tProject(t.myRole === 'LEADER' ? 'project.team.role.LEADER' : 'project.team.role.MEMBER')}
                    </span>
                    {tProject('project.team.member_count', { n: t.memberCount })}
                    {' · '}
                    {tProject('project.detail.projects.title')} {t.projectCount}
                  </span>
                  {(memberPreview[t.id]?.length ?? 0) > 0 && (
                    <AvatarStack members={memberPreview[t.id]!} total={t.memberCount} />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* 우 — 선택 팀 패널 */}
        <section className="pj-teams-detail-panel pj-teams-detail-panel--cards scrollbar-thin" aria-label={selected?.name ?? tProject('project.team.detail.select_hint')}>
          {!selected && <div className="pj-empty" role="status">{tProject('project.team.detail.select_hint')}</div>}
          {selected && (
            <>
              {/* v2.2 — 기능 구분 강화 (A안): 헤더/멤버/프로젝트 독립 섹션 카드 */}
              <div className="pj-team-section-card pj-team-section-card--head">
              <div className="pj-team-panel-head">
                {!editing ? (
                  <>
                    <div className="pj-team-panel-title">
                      <h2 className="pj-section-heading">{selected.name}</h2>
                      {selected.description && <p className="pj-team-desc">{selected.description}</p>}
                    </div>
                    {isLeader && (
                      <div className="pj-team-settings" ref={menuRef}>
                        <button
                          type="button"
                          className="pj-button pj-team-settings-trigger"
                          aria-label={tProject('project.team.settings_aria')}
                          aria-expanded={menuOpen}
                          onClick={() => setMenuOpen((v) => !v)}
                        >
                          <Settings size={14} />
                        </button>
                        {menuOpen && (
                          <div className="pj-team-settings-menu" role="menu">
                            <button
                              type="button"
                              role="menuitem"
                              className="pj-team-settings-item"
                              onClick={() => {
                                setMenuOpen(false);
                                setEditing(true);
                                setEditName(selected.name);
                                setEditDesc(selected.description ?? '');
                              }}
                            >
                              <Edit2 size={13} />
                              {tProject('project.team.edit')}
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              className="pj-team-settings-item pj-team-settings-item--danger"
                              onClick={() => {
                                setMenuOpen(false);
                                void handleDeleteTeam();
                              }}
                            >
                              <Trash2 size={13} />
                              {tProject('project.team.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <form className="pj-team-edit-form" onSubmit={(e) => void handleSaveMeta(e)}>
                    <label>
                      {tProject('project.team.create.label_name')}
                      <input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        maxLength={100}
                        required
                      />
                    </label>
                    <label>
                      {tProject('project.team.create.label_desc')}
                      <textarea value={editDesc} onChange={(e) => setEditDesc(e.target.value)} rows={2} maxLength={5000} />
                    </label>
                    <div className="pj-team-edit-actions">
                      <button type="button" className="pj-button" onClick={() => setEditing(false)} disabled={saving}>
                        {tProject('project.btn.cancel')}
                      </button>
                      <button type="submit" className="pj-button pj-button--primary" disabled={saving || !editName.trim()}>
                        {saving ? tProject('project.btn.saving') : tProject('project.btn.save')}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {actionError && (
                <div className="pj-error" role="alert"><AlertCircle size={14} /> {actionError}</div>
              )}
              </div>

              <div className="pj-team-section-card">
              <div className="pj-team-members-head">
                <h3 className="pj-section-subheading pj-team-section-title">
                  <span className="pj-team-section-icon" aria-hidden="true"><Users size={13} /></span>
                  {tProject('project.team.members')} ({selected.memberCount})
                </h3>
                {isLeader && (
                  <button type="button" className="pj-invite-button" onClick={() => setInviting(true)}>
                    <UserPlus size={13} />
                    {tProject('project.team.invite')}
                  </button>
                )}
              </div>
              {membersLoading ? (
                <div className="pj-loading" role="status">…</div>
              ) : (
                <ul className="pj-member-grid">
                  {members.map((m) => {
                    const isSelf = me !== null && String(me.id) === m.userId;
                    return (
                      <li key={m.userId} className="pj-member-card">
                        {m.avatarUrl ? (
                          <img className="pj-member-card-avatar" src={m.avatarUrl} alt="" />
                        ) : (
                          <span className="pj-member-card-avatar pj-member-card-avatar--initial" aria-hidden="true">
                            {m.name.slice(0, 1)}
                          </span>
                        )}
                        <span className="pj-member-card-name" title={m.email}>{m.name}</span>
                        {isLeader ? (
                          /* v2.4 (2026-06-11) — 셀렉트 규격화: shadcn/ui Select (tailwind utilities-only) */
                          <Select
                            value={m.role}
                            onValueChange={(v) => void handleRoleChange(m, v as ProjectTeamRole)}
                          >
                            <SelectTrigger
                              size="sm"
                              aria-label={tProject('project.team.role_change_aria', { name: m.name })}
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="LEADER">{tProject('project.team.role.LEADER')}</SelectItem>
                              <SelectItem value="MEMBER">{tProject('project.team.role.MEMBER')}</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`pj-role-badge${m.role === 'LEADER' ? ' pj-role-badge--leader' : ''}`}>
                            {tProject(m.role === 'LEADER' ? 'project.team.role.LEADER' : 'project.team.role.MEMBER')}
                          </span>
                        )}
                        {(isLeader || isSelf) && (
                          <button
                            type="button"
                            className="pj-button pj-button--small"
                            onClick={() => void handleRemove(m, isSelf)}
                          >
                            {isSelf ? <LogOut size={12} /> : null}
                            {isSelf ? tProject('project.team.member.leave') : tProject('project.team.member.remove')}
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              </div>

              <div className="pj-team-section-card">
              <div className="pj-team-members-head">
                <h3 className="pj-section-subheading pj-team-section-title">
                  <span className="pj-team-section-icon pj-team-section-icon--green" aria-hidden="true"><Briefcase size={13} /></span>
                  {tProject('project.team.detail.projects_heading')} ({selectedProjects.length})
                </h3>
                <button
                  type="button"
                  className="pj-invite-button"
                  onClick={() => navigate(`/task?team=${selected.id}`)}
                  title={tProject('project.team.open_in_projects')}
                >
                  <Briefcase size={13} />
                  {tProject('project.team.open_in_projects')}
                </button>
                <button type="button" className="pj-invite-button" onClick={() => setCreatingProject(true)}>
                  <Plus size={13} />
                  {tProject('project.list.add')}
                </button>
              </div>
              {selectedProjects.length === 0 ? (
                <div className="pj-empty" role="status">{tProject('project.team.detail.no_projects')}</div>
              ) : (
                <ul className="pj-team-mini-projects">
                  {selectedProjects.slice(0, 4).map((p) => {
                    const pct = p.taskCount > 0
                      ? Math.round(((p.doneTaskCount ?? 0) / p.taskCount) * 100)
                      : 0;
                    // v2.9.8 — 생성자 본인만 휴지통 노출 (서버 FORBIDDEN_NOT_CREATOR 의 UI 미러)
                    const deletable = isProjectCreator(p);
                    return (
                      <li key={p.id} className={deletable ? 'pj-row-li--actions' : undefined}>
                        <button
                          type="button"
                          className="pj-team-mini-project"
                          onClick={() => navigate(`/task?team=${selected.id}&project=${p.id}`)}
                          aria-label={tProject('project.list.open_aria', { name: p.name })}
                        >
                          <span className="pj-team-mini-project-name">
                            <Briefcase size={13} />
                            {p.name}
                          </span>
                          <span className="pj-team-mini-project-side">
                            <span className="pj-progress" aria-hidden="true">
                              <span className="pj-progress-fill" style={{ width: `${pct}%` }} />
                            </span>
                            <span className="pj-progress-num">{pct}%</span>
                            <ArrowRight size={13} />
                          </span>
                        </button>
                        {deletable && (
                          <button
                            type="button"
                            className="pj-row-del"
                            aria-label={tProject('project.list.delete_aria', { name: p.name })}
                            onClick={() => {
                              void confirmAndDeleteProject(p).then((deleted) => {
                                if (deleted) { void refreshProjects(); void refreshTeams(); }
                              });
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              <button
                type="button"
                className="pj-team-projects-link"
                onClick={() => navigate(`/project/projects?team=${selected.id}`)}
              >
                <Briefcase size={14} />
                {tProject('project.team.detail.projects_link', { n: selected.projectCount })}
              </button>
              </div>
            </>
          )}
        </section>
      </div>
      )}

      {createModal.shouldRender && (
        <TeamCreateInviteModal
          isLeaving={createModal.isLeaving}
          onClose={() => setCreating(false)}
          onCreated={(team) => {
            setCreating(false);
            setTeams((prev) => (prev.some((t) => t.id === team.id) ? prev : [team, ...prev]));
            setSelectedTeamId(team.id);
            // 생성과 함께 멤버가 들어왔을 수 있음 — 카운트·미니카드 전체 갱신
            void refreshTeams();
            void refreshProjects();
          }}
        />
      )}
      {inviteModal.shouldRender && inviteModal.latched?.teamId && (
        <TeamMemberInviteModal
          teamId={inviteModal.latched.teamId}
          isLeaving={inviteModal.isLeaving}
          onClose={() => setInviting(false)}
          onAdded={() => {
            if (selectedTeamId) void refreshMembers(selectedTeamId);
            void refreshTeams();
          }}
        />
      )}
      {projectModal.shouldRender && (
        <ProjectCreateSimpleModal
          teams={teams}
          initialTeamId={projectModal.latched?.teamId ?? null}
          isLeaving={projectModal.isLeaving}
          onClose={() => setCreatingProject(false)}
          onCreated={() => {
            setCreatingProject(false);
            // 미니 카드·진행률 링·projectCount 동시 갱신.
            void refreshProjects();
            void refreshTeams();
          }}
        />
      )}
    </div>
  );
}
